export interface ApiProblem {
  code: string;
  message: string;
  requestId: string;
  fieldErrors?: Record<string, string>;
}

export class ClientApiError extends Error {
  constructor(
    public readonly problem: ApiProblem,
    public readonly status: number,
  ) {
    super(problem.message);
    this.name = "ClientApiError";
  }
}

export interface SessionPrincipal {
  userId: string;
  email: string;
  emailVerified: boolean;
  status: "pending" | "active" | "suspended" | "disabled";
  displayName: string;
  publicSlug: string;
  onboardingComplete: boolean;
  roles: string[];
  permissions: string[];
  expiresAt: string;
}

export interface SessionResponse {
  authenticated: boolean;
  principal: SessionPrincipal | null;
  requestId: string;
}

export interface PublicConfig {
  appName: string;
  baseUrl: string;
  locale: string;
  timezone: string;
  turnstileSiteKey: string;
  turnstileRequired: boolean;
  googleOAuthEnabled: boolean;
  termsVersion: string;
  privacyVersion: string;
}

function apiProblemFromPayload(payload: unknown): ApiProblem | null {
  if (!payload || typeof payload !== "object" || !("error" in payload)) return null;
  const error = payload.error;
  if (!error || typeof error !== "object") return null;
  if (!("code" in error) || typeof error.code !== "string") return null;
  if (!("message" in error) || typeof error.message !== "string") return null;
  if (!("requestId" in error) || typeof error.requestId !== "string") return null;
  return error as ApiProblem;
}

async function parseResponse<T>(response: Response): Promise<T> {
  const payload = (await response.json().catch(() => null)) as T | unknown;
  if (!response.ok) {
    const requestId = response.headers.get("x-request-id") ?? "unknown";
    const problem = apiProblemFromPayload(payload) ?? {
      code: "REQUEST_FAILED",
      message: "Không thể xử lý yêu cầu.",
      requestId,
    };
    throw new ClientApiError(problem, response.status);
  }
  return payload as T;
}

export async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !(init.body instanceof FormData) && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  const response = await fetch(path, {
    ...init,
    headers,
    credentials: "same-origin",
    cache: "no-store",
  });
  return parseResponse<T>(response);
}

export function postJson<T>(path: string, body: Record<string, unknown>): Promise<T> {
  return apiRequest<T>(path, { method: "POST", body: JSON.stringify(body) });
}

export function deleteRequest<T>(path: string): Promise<T> {
  return apiRequest<T>(path, { method: "DELETE" });
}

export function getSession(): Promise<SessionResponse> {
  return apiRequest<SessionResponse>("/api/v1/auth/session");
}

export function getPublicConfig(): Promise<PublicConfig & { requestId: string }> {
  return apiRequest<PublicConfig & { requestId: string }>("/api/v1/config");
}

export function safeReturnToFromLocation(): string {
  const value = new URLSearchParams(window.location.search).get("returnTo");
  if (!value?.startsWith("/") || value.startsWith("//") || value.includes("\\")) {
    return "/member";
  }
  return value;
}
